/**
 * Sentry Configuration
 *
 * Initializes Sentry for error tracking in production.
 * Uses VITE_SENTRY_DSN environment variable.
 * Disabled in development by default.
 *
 * @module lib/sentry
 */

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN

// Determine if we should initialize Sentry
// - Must have a DSN
// - Must be in production (or explicitly forced)
const shouldInit = SENTRY_DSN && import.meta.env.PROD

let sentryInitialized = false
let SentryModule: any = null

/**
 * Initialize Sentry error tracking.
 * Call this once during app startup.
 */
export async function initSentry(): Promise<void> {
  if (!shouldInit || sentryInitialized) {
    return
  }

  try {
    // Dynamic import to avoid bundling Sentry in development builds unnecessarily
    const sentry = await import('@sentry/react')
    SentryModule = sentry.default || sentry

    SentryModule.init({
      dsn: SENTRY_DSN,
      environment: import.meta.env.MODE || 'production',
      tracesSampleRate: 0.1,  // Sample 10% of transactions for performance monitoring
      replaysSessionSampleRate: 0.1,  // Sample 10% of sessions for session replay
      beforeSend(event, hint) {
        // Filter out sensitive data
        if (event.request) {
          // Remove authorization headers
          if (event.request.headers) {
            delete event.request.headers.authorization
          }
        }

        // Filter out sensitive errors (like password validation)
        if (event.exception?.values?.[0]?.value) {
          const message = event.exception.values[0].value
          if (typeof message === 'string' && (message.includes('password') || message.includes('credential'))) {
            // Don't send error with actual passwords
            return null
          }
        }

        return event
      },
    })

    sentryInitialized = true
    // Also set on window for compatibility with error-handler.ts
    if (typeof window !== 'undefined') {
      (window as any).Sentry = SentryModule
    }
    console.log('[Sentry] Initialized')
  } catch (error) {
    console.warn('[Sentry] Failed to initialize:', error)
  }
}

/**
 * Manually capture an exception.
 * Useful for non-automatic error reporting.
 */
export function captureException(error: Error, context?: Record<string, any>): void {
  if (sentryInitialized && SentryModule) {
    SentryModule.captureException(error, {
      extra: context,
    })
  }
}

/**
 * Capture a user feedback event (e.g., user reported issue).
 */
export function captureUserFeedback(message: string, email?: string): void {
  if (sentryInitialized && SentryModule) {
    SentryModule.showReportDialog({
      message,
      email,
    })
  }
}
